/**
 * Request Coalescer
 * Batches and deduplicates API requests to reduce network overhead
 * @version 1.0.0
 */

class RequestCoalescer {
  constructor() {
    this.pendingRequests = new Map();
    this.requestQueue = new Map();
    this.cache = new Map();
    this.config = {
      batchDelay: 50, // ms to wait before sending batch
      cacheTimeout: 60000, // 1 minute cache
      maxBatchSize: 10,
      enableCache: true
    };
    this.stats = {
      requests: 0,
      coalesced: 0,
      cached: 0,
      batched: 0
    };
    this.init();
  }

  init() {
    console.log('🔄 Request Coalescer initialized');

    // Intercept fetch if available
    if (typeof window.fetch !== 'undefined') {
      this.interceptFetch();
    }

    // Clean cache periodically
    setInterval(() => this.cleanCache(), this.config.cacheTimeout);
  }

  /**
   * Intercept fetch API to enable automatic coalescing
   */
  interceptFetch() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function(...args) {
      const [url, options = {}] = args;

      // Only coalesce GET requests to same origin
      const shouldCoalesce = (
        !options.method || options.method.toUpperCase() === 'GET'
      ) && self.isCoalesceable(url);

      if (shouldCoalesce) {
        return self.fetch(url, options);
      }

      // Pass through other requests
      return originalFetch.apply(this, args);
    };

    console.log('✅ Fetch API intercepted for coalescing');
  }

  /**
   * Check if URL should be coalesced
   */
  isCoalesceable(url) {
    try {
      const parsed = new URL(url, window.location.origin);

      // Only same-origin API requests
      if (parsed.origin !== window.location.origin) return false;

      // Common API endpoints
      const coalescePaths = [
        '/cart',
        '/products',
        '/collections',
        '/search',
        '/recommendations',
        '/customer'
      ];

      return coalescePaths.some(path => parsed.pathname.includes(path));
    } catch (e) {
      return false;
    }
  }

  /**
   * Coalesced fetch
   */
  fetch(url, options = {}) {
    this.stats.requests++;

    const requestKey = this.getRequestKey(url, options);

    // Check cache first
    if (this.config.enableCache) {
      const cached = this.getCached(requestKey);
      if (cached) {
        this.stats.cached++;
        console.log('💾 Returning cached response:', url);
        return Promise.resolve(cached.clone());
      }
    }

    // Check if identical request is already pending
    if (this.pendingRequests.has(requestKey)) {
      this.stats.coalesced++;
      console.log('🔗 Coalescing duplicate request:', url);
      return this.pendingRequests.get(requestKey);
    }

    // Create new request promise
    const requestPromise = this.executeRequest(url, options, requestKey);

    // Store as pending
    this.pendingRequests.set(requestKey, requestPromise);

    // Clean up after resolution
    requestPromise.finally(() => {
      this.pendingRequests.delete(requestKey);
    });

    return requestPromise;
  }

  /**
   * Execute the actual request
   */
  async executeRequest(url, options, requestKey) {
    try {
      const response = await window.originalFetch(url, options);

      // Cache successful responses
      if (response.ok && this.config.enableCache) {
        const cloned = response.clone();
        this.cache.set(requestKey, {
          response: cloned,
          timestamp: Date.now()
        });
      }

      return response;

    } catch (error) {
      console.error('❌ Request failed:', url, error);
      throw error;
    }
  }

  /**
   * Get cached response
   */
  getCached(requestKey) {
    const cached = this.cache.get(requestKey);

    if (!cached) return null;

    // Check if cache is expired
    const age = Date.now() - cached.timestamp;
    if (age > this.config.cacheTimeout) {
      this.cache.delete(requestKey);
      return null;
    }

    return cached.response;
  }

  /**
   * Clean expired cache entries
   */
  cleanCache() {
    const now = Date.now();
    let cleaned = 0;

    this.cache.forEach((cached, key) => {
      const age = now - cached.timestamp;
      if (age > this.config.cacheTimeout) {
        this.cache.delete(key);
        cleaned++;
      }
    });

    if (cleaned > 0) {
      console.log(`🧹 Cleaned ${cleaned} expired cache entries`);
    }
  }

  /**
   * Generate request key for caching/deduplication
   */
  getRequestKey(url, options = {}) {
    const normalized = new URL(url, window.location.origin).href;
    const method = (options.method || 'GET').toUpperCase();
    const body = options.body ? JSON.stringify(options.body) : '';

    return `${method}:${normalized}:${body}`;
  }

  /**
   * Batch multiple requests
   */
  async batchFetch(urls, options = {}) {
    this.stats.batched++;

    const batchKey = this.getBatchKey(urls);

    // Check if batch is already queued
    if (this.requestQueue.has(batchKey)) {
      console.log('⏳ Batch already queued, joining:', batchKey);
      return this.requestQueue.get(batchKey);
    }

    // Create batch promise
    const batchPromise = new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const results = await Promise.all(
            urls.map(url => this.fetch(url, options))
          );
          resolve(results);
        } catch (error) {
          reject(error);
        } finally {
          this.requestQueue.delete(batchKey);
        }
      }, this.config.batchDelay);
    });

    this.requestQueue.set(batchKey, batchPromise);

    console.log(`📦 Batching ${urls.length} requests`);
    return batchPromise;
  }

  /**
   * Generate batch key
   */
  getBatchKey(urls) {
    return urls.sort().join('|');
  }

  /**
   * Shopify-specific: Batch product requests
   */
  async batchProductFetch(productIds) {
    // Shopify allows fetching multiple products via collection JSON
    // Or individual product.json files

    console.log(`🛍️ Batching ${productIds.length} product requests`);

    // Option 1: Use collection with product IDs filter (if available)
    // Option 2: Parallel requests with coalescing

    const urls = productIds.map(id =>
      `/products/${id}.js`
    );

    return this.batchFetch(urls);
  }

  /**
   * Shopify-specific: Batch cart operations
   */
  async batchCartUpdate(updates) {
    // Combine multiple cart updates into single request
    console.log(`🛒 Batching ${updates.length} cart updates`);

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates: Object.fromEntries(updates) })
    });
  }

  /**
   * Debounced request (useful for search, autocomplete)
   */
  debounce(url, delay = 300) {
    const key = `debounce:${url}`;

    // Clear existing timeout
    if (this.pendingRequests.has(key)) {
      clearTimeout(this.pendingRequests.get(key).timeoutId);
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.fetch(url)
          .then(resolve)
          .catch(reject)
          .finally(() => {
            this.pendingRequests.delete(key);
          });
      }, delay);

      this.pendingRequests.set(key, { timeoutId, resolve, reject });
    });
  }

  /**
   * Prefetch and cache
   */
  async prefetch(url, options = {}) {
    const response = await this.fetch(url, options);
    console.log('⚡ Prefetched and cached:', url);
    return response;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
    this.requestQueue.clear();
    console.log('🧹 All caches cleared');
  }

  /**
   * Get statistics
   */
  getStats() {
    const totalRequests = this.stats.requests;
    const efficiency = totalRequests > 0
      ? Math.round(((this.stats.coalesced + this.stats.cached) / totalRequests) * 100)
      : 0;

    return {
      ...this.stats,
      efficiency: `${efficiency}%`,
      pendingRequests: this.pendingRequests.size,
      cacheSize: this.cache.size,
      queueSize: this.requestQueue.size
    };
  }

  /**
   * Get status
   */
  getStatus() {
    return {
      config: this.config,
      stats: this.getStats(),
      cache: {
        size: this.cache.size,
        keys: Array.from(this.cache.keys())
      }
    };
  }
}

// Store original fetch before interception
window.originalFetch = window.fetch;

// Initialize
window.requestCoalescer = new RequestCoalescer();

console.log('🔄 Request Coalescer ready:', window.requestCoalescer.getStats());

// Usage examples for developers
console.log(`
📚 Request Coalescer Usage:

1. Automatic (already enabled for GET requests):
   fetch('/products/123.js') // Automatically coalesced

2. Batch requests:
   requestCoalescer.batchFetch(['/products/1.js', '/products/2.js'])

3. Debounced search:
   requestCoalescer.debounce('/search?q=shirt', 300)

4. Prefetch:
   requestCoalescer.prefetch('/products/popular.js')

5. Batch product fetch:
   requestCoalescer.batchProductFetch(['product-1', 'product-2'])

6. Stats:
   requestCoalescer.getStats()
`);

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RequestCoalescer;
}
